import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Hidden,
  Divider,
} from "@mui/material";
import { AddCircleOutlined as AddIcon } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";

import { BreadcrumbContext } from "../../../../components/Breadcrumb";
import { useOrganizationCollections } from "../../../../hooks/useOrganizationCollections";
import AccountCard from "../../../../components/AccountCard";
import { 
  LOCAL_STORAGE_CURRENT_USER,
  LOCAL_STORAGE_CURRENT_ORG,
  getLocalStorage 
} from "../../../../utils/localStorage";
import { isKWALLAdmin } from "../../../../utils/index";
import { FirebaseAuth } from "../../../../components/FirebaseAuth/firebase";
import AggregatedTrafficChart from "../../../../components/AggregatedTrafficChart/AggregatedTrafficChart";

/**
 * Organization All Collections Page
 * 
 * This page displays all collections (accounts) within an organization.
 * It provides a unified view of all collections with proper permission handling,
 * using the same AccountCard component that was used on the home page.
 * 
 * Features:
 * - Displays all collections from the organization
 * - Permission-based access (admin vs regular user)
 * - Shows site counts for each collection
 * - "Create Collection" functionality
 * - Handles loading and error states
 * - Responsive grid layout
 * - Empty state with call-to-action
 */
const AllCollections = () => {
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  const { organizationId } = useParams();
  const history = useNavigate();
  
  // Get current user and organization data
  const userData = getLocalStorage(LOCAL_STORAGE_CURRENT_USER);
  const currentOrganization = getLocalStorage(LOCAL_STORAGE_CURRENT_ORG);
  const userRole = userData?.role;
  
  // Get stable userId directly from Firebase Auth
  const userId = FirebaseAuth.currentUser?.uid || "mock-user-id";
  
  // Use organization from localStorage if no organizationId param
  const effectiveOrgId = organizationId || currentOrganization?.id;

  // Call the hook with stable parameters
  const { collections, loading, error } = useOrganizationCollections(
    effectiveOrgId, 
    userId, 
    userRole
  );

  // Set up breadcrumb navigation
  useEffect(() => {
    setBreadcrumb([
      { to: "/", text: "Home", active: false },
      { to: null, text: "All Collections", active: true },
    ]);
  }, [setBreadcrumb]);

  // Handle loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  // Handle missing organization ID
  if (!effectiveOrgId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          All Collections
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          No organization selected. Please select an organization first.
        </Alert>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          All Collections
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading collections: {error}
        </Alert>
      </Container>
    );
  }

  // Render collections page
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {isKWALLAdmin(userRole) ? 'All Collections' : 'Your Collections'}
        </Typography>
        <Hidden smDown>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => history("/new-account")}
            sx={{ borderRadius: "8px" }}
          >
            Add New
          </Button>
        </Hidden>
      </Box>
      
      <Divider sx={{ mb: 3 }} />

      {/* Organization Traffic Analytics */}
      {effectiveOrgId && (
        <Box sx={{ mb: 4 }}>
          <AggregatedTrafficChart 
            level="organization" 
            levelId={effectiveOrgId} 
            title="Organization Traffic Analytics"
          />
        </Box>
      )}

      {/* Collections Content */}
      {collections.length > 0 ? (
        <>
          {/* Collections Grid */}
          <Grid container spacing={3}>
            {collections.map((account, i) => (
              <AccountCard
                i={i}
                account={account}
                key={account?.id}
                siteCount={account.siteCount || 0}
              />
            ))}
          </Grid>

          {/* Summary information */}
          <Box sx={{ mt: 4, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {collections.length} collection{collections.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </>
      ) : (
        /* Empty State */
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 5,
                borderRadius: 2,
                border: "1px dashed rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AddIcon
                sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                No collections created yet
              </Typography>
              <Typography
                variant="body2"
                color="text.disabled"
                sx={{ mb: 3, textAlign: "center" }}
              >
                Create a collection to organize and manage your websites
              </Typography>
              <Button
                variant="contained"
                onClick={() => history("/new-account")}
                sx={{ borderRadius: "8px" }}
              >
                Create Your First Collection
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AllCollections;